import { postBugReport } from 'api/BugReport/actions'
import classNames from 'classnames'
import { CommonButton } from 'CommonButton'
import html2canvas from 'html2canvas'
import { LoadingOverlay } from 'LoadingOverlay'
import type { SyntheticEvent } from 'react'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import type { State } from 'store/types'
import Transition from 'utils/SlideTransition'

import CloseIcon from '@mui/icons-material/Close'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import type { InputLabelProps } from '@mui/material/InputLabel'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'

import { toggleReportBugDialog } from './actions'
import useStyles from './useStyles'

function ReportBugDialog() {
  const { t: translate } = useTranslation()
  const dispatch = useDispatch()
  const [isRendering, setIsRendering] = useState(true)
  const [image, setImage] = useState<string>('')
  const classes = useStyles()
  const { errorCode, isLoading } = useSelector((state: State) => state.bugReport)
  const isOpen = useSelector((state: State) => state.isReportBugDialogOpen)
  const toggleModal = useCallback(() => dispatch(toggleReportBugDialog()), [dispatch])
  const [summary, setSummary] = React.useState('')
  const handleChangeSummary = (event: React.ChangeEvent<HTMLInputElement>) => setSummary(event.target.value)
  const [description, setDescription] = React.useState<string>('')
  const handleChangeDescription = (event: React.ChangeEvent<HTMLInputElement>) => setDescription(event.target.value)
  const disabled = !summary || !description || !image || isRendering
  const inputLabelProps: Partial<InputLabelProps> = {
    className: classes.inputLabel,
  }
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault()

    if (!summary || !description || !image) return

    dispatch(
      postBugReport({
        image: image.replace('data:image/png;base64,', ''),
        summary,
        description,
      }),
    )
  }
  const onClose = useCallback(() => {
    setSummary('')
    setDescription('')
    setImage('')
    toggleModal()
  }, [toggleModal])

  useEffect(() => {
    if (isOpen) {
      async function updateLastScreenshot() {
        const targetElement = (document.getElementById('root-utargi-web') as HTMLElement) || document.body
        const canvas = await html2canvas(targetElement)

        setImage(canvas.toDataURL('image/png'))
        setIsRendering(false)
      }

      setIsRendering(true)
      updateLastScreenshot()
    }
  }, [isOpen])

  // On success -> clean up, close dialog
  useEffect(() => {
    if (isOpen && !errorCode && !isLoading) {
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  return (
    <Dialog fullScreen TransitionComponent={Transition} open={isOpen} onClose={onClose}>
      <LoadingOverlay isLoading={isLoading}>
        <Box component="form" onSubmit={onSubmit}>
          <AppBar className={classes.appBar}>
            <Toolbar className={classes.toolbar}>
              <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                <CloseIcon />
              </IconButton>
              <Typography className={classes.title} sx={{ flex: 1 }} variant="h6" component="div">
                {translate('menu.reportBug')}
              </Typography>
              <CommonButton disabled={disabled} isUpperCase={false} type="submit">
                {translate('ReportBugDialog.report')}
              </CommonButton>
            </Toolbar>
          </AppBar>
          <Box m="1.5rem">
            <Box>
              <TextField
                fullWidth
                InputLabelProps={inputLabelProps}
                label={translate('ReportBugDialog.title')}
                name="summary"
                onChange={handleChangeSummary}
                required
                type="text"
                value={summary}
                variant="standard"
              />
            </Box>
            <Box>
              <TextField
                className={classes.fieldDetails}
                fullWidth
                InputLabelProps={inputLabelProps}
                label={translate('ReportBugDialog.description')}
                multiline
                name="details"
                onChange={handleChangeDescription}
                required
                type="text"
                value={description}
                variant="standard"
              />
            </Box>
          </Box>
          <LoadingOverlay isLoading={isRendering}>
            <Box minHeight={isRendering ? '6rem' : '0'} className={classNames({ [classes.imageWrapper]: !isRendering })}>
              {Boolean(image) && <img className={classes.screenshot} src={image} alt="" />}
            </Box>
          </LoadingOverlay>
        </Box>
      </LoadingOverlay>
    </Dialog>
  )
}

export default ReportBugDialog
